import { Group } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { useCallback, useEffect, useState } from 'react';
import i18n from '../../../../../base-dictionary';
import { exportChart } from '../../../../../helpers/image-downloader/image-downloader';
import { useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import { baseWhite, skyDark } from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Radio } from '../../../../../ui-library/radio/radio';
import { Tooltip } from '../../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../../ui-library/typography/typography';
import { Metric } from '../../../strategy-v2.type';
import { ReactNode, RefObject } from 'react';

type Props = {
  title: string | null | ReactNode;
  metric?: Metric;
  setMetric?: (metric: Metric) => void;
  chartRef?: RefObject<HTMLDivElement>;
  hideTitle?: boolean;
  disableControls?: boolean;
};

export const MainSectionControls = ({
  title,
  metric,
  setMetric,
  chartRef,
  hideTitle = false,
  disableControls = false,
}: Props) => {
  const { ref, width } = useElementSize();
  const [titleText, setTitleText] = useState<string>('');
  const [isOverflowing, setIsOverflowing] = useState(false);

  const getTruncatedTitle = useCallback((text: string, maxLength = 40) => {
    if (text.length <= maxLength) {
      return text;
    }

    return `${text.substring(0, maxLength)}...`;
  }, []);

  useEffect(() => {
    if (typeof title === 'string' && width > 0) {
      setTitleText(getTruncatedTitle(title, width / 16));
      setIsOverflowing(title.length > width / 16);
    }
  }, [title, width, getTruncatedTitle]);

  const tab = useStrategyStore((state) => state.performanceTab);

  const handleExportChart = async () => {
    if (!chartRef?.current) return;

    try {
      await exportChart(chartRef.current, `${tab}_chart.png`);
    } catch (error) {
      console.error('Error exporting chart:', error);
    }
  };

  const getTitle = () => {
    if (hideTitle) {
      return null;
    }

    if (typeof title === 'string') {
      return (
        <Tooltip
          variant="white"
          label={title}
          disabled={!isOverflowing}
          position="top-start"
          multiline
          style={{ maxWidth: 400 }}
        >
          <div
            style={{
              flex: '1',
              minWidth: 0,
              overflow: 'hidden',
              cursor: isOverflowing ? 'pointer' : 'default',
            }}
          >
            <Text
              size="large"
              weight="bold"
              style={{
                whiteSpace: 'nowrap',
                display: 'block',
                lineHeight: '30px',
              }}
            >
              {titleText}
            </Text>
          </div>
        </Tooltip>
      );
    }

    return (
      <div
        style={{
          flex: '1',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {title}
      </div>
    );
  };

  return (
    <Group justify="space-between" w="100%" h={30} mih={30} ref={ref}>
      {getTitle()}
      <Group gap={16} w="fit-content">
        {metric && setMetric && (
          <>
            <Radio
              label={i18n.t('strategy.initiatives.metrics.tasks')}
              checked={disableControls ? false : metric === Metric.Tasks}
              onChange={() => setMetric(Metric.Tasks)}
              disabled={disableControls}
            />
            <Radio
              label={i18n.t('strategy.initiatives.metrics.points')}
              checked={disableControls ? false : metric === Metric.Points}
              onChange={() => setMetric(Metric.Points)}
              disabled={disableControls}
            />
          </>
        )}
        {chartRef && (
          <Button
            variant="secondary"
            radius="xl"
            size="xs"
            leftSection={
              <Icon
                name="download"
                color={disableControls ? skyDark : baseWhite}
                variant="filled"
                size={16}
                style={{ cursor: disableControls ? 'not-allowed' : 'pointer' }}
              />
            }
            onClick={handleExportChart}
            disabled={disableControls}
          >
            {i18n.t('common.actions.export')}
          </Button>
        )}
      </Group>
    </Group>
  );
};
